import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, InputSearch, SelectPickerInput } from "@shared/components";
import { changeListeners, FieldErrorMessage, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { Process, UserProcedure } from "@shared/models";
import { Option } from "@shared/interfaces";
import { AssignProcedureDto, AssignProcedureFormShape } from "@containers/User/interfaces/UserForm.interface";

import { getInitValues, prepareSubmitValue, validationSchema } from "./formHelpers";

import "./index.scss";

const FORM_ID = "assign-procedure-form";

interface AssignProcedureFormProps {
  processesWithProcedures: Process[];
  assignedProcedures: UserProcedure[];
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: Omit<AssignProcedureDto, "user_id">) => void;
}

interface AssignProcedureFormFieldsProps extends AssignProcedureFormProps {
  formValues: AssignProcedureFormShape;
}

/** The form body: owns the form instance, built once per set of initial values (see `AssignProcedureForm`). */
const AssignProcedureFormFields: React.FunctionComponent<AssignProcedureFormFieldsProps> = (props) => {
  const { onChangeForm, onCloseForm, onSubmitForm, processesWithProcedures, assignedProcedures, formValues } = props;
  const [procedureOptions, setProcedureOptions] = useState<Option<number>[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedProcess, setSelectedProcess] = useState<number | null>(null);

  const form = useAppForm({
    defaultValues: formValues,
    validators: schemaValidators(validationSchema),
    listeners: changeListeners(onChangeForm),
    onSubmit: ({ value }) => {
      onSubmitForm(prepareSubmitValue(validationSchema.parse(value)));
    },
  });

  const processOptions = useMemo(() => {
    if (!processesWithProcedures.length) return [];

    const processOpt = processesWithProcedures.map((i) => {
      return {
        label: i.name,
        value: i.id,
      };
    });

    return [...processOpt, { label: "All", value: 0 }];
  }, [processesWithProcedures]);

  useEffect(() => {
    if (!(selectedProcess || selectedProcess === 0)) {
      setProcedureOptions([]);
      return;
    }

    const filteredProcesses = selectedProcess
      ? processesWithProcedures.filter((process) => process.id === selectedProcess)
      : [...processesWithProcedures];

    const procedureOptions = filteredProcesses.reduce((acc, process) => {
      const options: Option<number>[] = (process?.procedures || []).map((i) => ({
        label: i.name,
        value: i.id,
      }));
      return [...acc, ...options];
    }, [] as Option<number>[]);

    const filteredProcedureOptions = searchValue
      ? procedureOptions.filter((procedure) => procedure.label.toLowerCase().includes(searchValue.toLowerCase()))
      : procedureOptions;

    setProcedureOptions(filteredProcedureOptions);
  }, [searchValue, selectedProcess, processesWithProcedures]);

  useEffect(() => {
    if (processesWithProcedures.length && processesWithProcedures[0].procedures?.length) {
      const procedureOpt = processesWithProcedures[0].procedures.map((i) => {
        return {
          label: i.name,
          value: i.id,
        };
      });

      setProcedureOptions(procedureOpt);
    }
  }, [processesWithProcedures]);

  const availableProcedureOptions = useMemo(
    () =>
      procedureOptions.filter(
        (procedure) =>
          !assignedProcedures.find((assignedProcedure) => assignedProcedure.procedure.id === procedure.value),
      ),
    [procedureOptions, assignedProcedures],
  );

  const handleSelectProcess = useCallback(
    (value: number) => {
      setSelectedProcess(value);
      form.setFieldValue("process", value);
    },
    [form],
  );

  return (
    <div className="assign-procedure-form">
      <form id={FORM_ID} onSubmit={formSubmitHandler(form)}>
        <div className="assign-procedure-form-content">
          <div className="procedure-process-input-wrapper">
            <div className="procedure-process-input procedure-search-wrapper">
              <InputSearch
                name="search"
                onChangeSearch={setSearchValue}
                labelTitle="Process"
                placeholder="Search procedure"
              />
            </div>
            <div className="procedure-process-input select-wrapper">
              {/* The picker also drives the procedure list (`handleSelectProcess`), so it is bound by hand instead of through `field.Select`. */}
              <form.AppField name="process">
                {(field) => (
                  <SelectPickerInput
                    searchable={false}
                    name={field.name}
                    cleanable={false}
                    data={processOptions}
                    onChange={handleSelectProcess}
                    labelTitle="Process"
                    value={field.state.value}
                    placeholder="Process"
                  />
                )}
              </form.AppField>
            </div>
          </div>
          <div className="procedures-wrapper">
            <div className="procedures-header">Procedures</div>
            {!availableProcedureOptions.length ? (
              <div className="no-procedures">No procedures yet.</div>
            ) : (
              <form.AppField name="procedures" mode="array">
                {(field) => (
                  <>
                    <div className="procedures-body">
                      {availableProcedureOptions.map((procedureOption) => (
                        <label className="procedure-label" key={procedureOption.value}>
                          <input
                            name="procedures"
                            type="checkbox"
                            className="procedure-checkbox"
                            value={procedureOption.value}
                            checked={field.state.value.includes(procedureOption.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                field.pushValue(procedureOption.value);
                              } else {
                                field.removeValue(field.state.value.indexOf(procedureOption.value));
                              }
                            }}
                          />
                          <div className="procedure-label-text">{procedureOption.label}</div>
                        </label>
                      ))}
                    </div>
                    <FieldErrorMessage />
                  </>
                )}
              </form.AppField>
            )}
          </div>
        </div>
      </form>

      <div className="assign-procedure-form-footer">
        <Button type="button" className="close-btn margin-right" onClick={() => onCloseForm()}>
          Cancel
        </Button>
        {/* Raw `form.Subscribe`: the footer sits outside every field, so no field context supplies the checked count. */}
        <form.Subscribe selector={(state) => state.values.procedures.length === 0}>
          {(noProcedureChecked) => (
            <Button type="submit" form={FORM_ID} className="accent padding" disabled={noProcedureChecked}>
              Assign Procedure
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  );
};

/** Keyed on the initial values: a process list that arrives or changes while the form is open mounts a fresh form. */
const AssignProcedureForm: React.FunctionComponent<AssignProcedureFormProps> = (props) => {
  const formValues = useMemo(() => getInitValues(props.processesWithProcedures), [props.processesWithProcedures]);

  return <AssignProcedureFormFields key={JSON.stringify(formValues)} {...props} formValues={formValues} />;
};

export default AssignProcedureForm;
